import { supabase } from '@/lib/supabase';

export interface ProductStock {
  product_id: string;
  date: string;
  initial_stock: number;
  current_stock: number;
  sold_stock: number;
  version: number;
}


/**
 * 상품 재고 관리 서비스
 */
export const stockService = {
  /**
   * 상품의 현재 재고를 조회합니다 (fallback 로직 포함)
   */
  async getProductStock(productId: string, date?: string, allowFallback: boolean = true): Promise<ProductStock | null> {
    try {
      const targetDate = date || new Date().toISOString().split('T')[0];
      
      // 마감할인의 경우 fallback을 사용하지 않고 당일 재고만 조회
      if (!allowFallback) {
        const { data, error } = await supabase
          .from('product_stock')
          .select('*')
          .eq('product_id', productId)
          .eq('date', targetDate)
          .single();
        
        if (error) {
          console.error('Error fetching product stock:', error);
          return null;
        }
        
        return data;
      }
      
      // 직접 product_stock 테이블 조회 (RPC 함수 대신)
      const { data, error } = await supabase
        .from('product_stock')
        .select('*')
        .eq('product_id', productId)
        .eq('date', targetDate)
        .single();

      if (error) {
        // 해당 날짜 데이터가 없으면 products 테이블의 stock_quantity로 fallback
        if (error.code === 'PGRST116') { // No rows found
          const { data: productData, error: productError } = await supabase
            .from('products')
            .select('stock_quantity')
            .eq('id', productId)
            .single();
          
          if (productError) {
            console.error('Error fetching product data:', productError);
            // Return a safe fallback with zero stock if product not found
            return {
              product_id: productId,
              date: targetDate,
              initial_stock: 0,
              current_stock: 0,
              sold_stock: 0,
              version: 1
            } as ProductStock;
          }
          
          // products 테이블의 stock_quantity를 사용하여 기본 재고 생성
          const stockQuantity = productData.stock_quantity || 0;
          return {
            product_id: productId,
            date: targetDate,
            initial_stock: stockQuantity,
            current_stock: stockQuantity,
            sold_stock: 0,
            version: 1
          } as ProductStock;
        }
        
        // Handle RLS or permission errors gracefully
        if (error.code === '42501' || error.message.includes('permission denied') || error.message.includes('policy')) {
          console.warn('Permission denied accessing product_stock, using fallback');
          // Try to get product info and return fallback stock
          const { data: productData } = await supabase
            .from('products')
            .select('stock_quantity')
            .eq('id', productId)
            .single();
          
          const stockQuantity = productData?.stock_quantity || 0;
          return {
            product_id: productId,
            date: targetDate,
            initial_stock: stockQuantity,
            current_stock: stockQuantity,
            sold_stock: 0,
            version: 1
          } as ProductStock;
        }
        
        console.error('Error fetching product stock:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getProductStock:', error);
      return null;
    }
  },

  /**
   * 여러 상품의 재고를 한 번에 조회합니다
   */
  async getMultipleProductStock(productIds: string[], date?: string, allowFallback: boolean = true): Promise<Map<string, ProductStock>> {
    const stockMap = new Map<string, ProductStock>();
    
    try {
      // 병렬로 모든 상품의 재고를 조회
      const stockPromises = productIds.map(productId => 
        this.getProductStock(productId, date, allowFallback)
      );
      
      const stockResults = await Promise.all(stockPromises);
      
      productIds.forEach((productId, index) => {
        const stock = stockResults[index];
        if (stock) {
          stockMap.set(productId, stock);
        }
      });
      
      return stockMap;
    } catch (error) {
      console.error('Error in getMultipleProductStock:', error);
      return stockMap;
    }
  },

  /**
   * 장바구니 상품들의 재고를 검증합니다
   */
  async validateCartStock(
    cartItems: Array<{ productId: string; quantity: number; productName: string }>,
    orderType?: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER'
  ): Promise<{
    isValid: boolean;
    errors: string[];
    stockData: Map<string, ProductStock>;
  }> {
    const errors: string[] = [];
    const productIds = cartItems.map(item => item.productId);
    
    // 당일 재고 데이터 확인 (자동 생성은 백엔드 cron job에서 처리)
    if (orderType !== 'DEADLINE_DISCOUNT') {
      try {
        const targetDate = new Date().toISOString().split('T')[0];
        
        // 첫 번째 상품의 재고를 체크해서 당일 데이터가 있는지 확인
        if (productIds.length > 0) {
          const { data: existingStock } = await supabase
            .from('product_stock')
            .select('id')
            .eq('product_id', productIds[0])
            .eq('date', targetDate)
            .maybeSingle();
          
          // 당일 재고 데이터가 없으면 로그만 남김 (백엔드에서 자동 생성됨)
          if (!existingStock) {
            console.log('당일 재고 데이터가 없습니다. 백엔드 자동 생성을 기다립니다.');
          }
        }
      } catch (error) {
        console.warn('당일 재고 데이터 확인 중 오류:', error);
        // 재고 확인 실패해도 검증은 계속 진행
      }
    }
    
    // 마감할인은 당일 재고만 허용 (fallback 없음)
    const allowFallback = orderType !== 'DEADLINE_DISCOUNT';
    const stockData = await this.getMultipleProductStock(productIds, undefined, allowFallback);
    
    for (const item of cartItems) {
      const stock = stockData.get(item.productId);
      
      // 예약할인은 수량 제한 없이 주문 가능 (initial_stock 기반)
      if (orderType === 'RESERVATION_DISCOUNT') {
        // 예약할인은 기본적으로 무제한 주문 가능
        // 단순히 상품이 존재하는지만 확인
        const { data: product, error } = await supabase
          .from('products')
          .select('id, is_available')
          .eq('id', item.productId)
          .single();
        
        if (error || !product) {
          console.warn(`Product not found for reservation discount: ${item.productName}`, error);
          // For reservation discounts, if we can't verify the product, allow it but warn
          errors.push(`${item.productName}의 상품 정보를 확인할 수 없지만 예약할인으로 주문 가능합니다`);
          continue;
        }
        
        if (!product.is_available) {
          errors.push(`${item.productName}은(는) 현재 주문할 수 없는 상품입니다`);
        }
        
        // 예약할인은 수량 제한 없음 - 체크하지 않음
      } else {
        // 마감할인과 일반주문은 current_stock 체크
        if (!stock) {
          if (orderType === 'DEADLINE_DISCOUNT') {
            errors.push(`${item.productName}의 마감할인 재고 정보를 찾을 수 없습니다`);
          } else {
            errors.push(`${item.productName}의 재고 정보를 찾을 수 없습니다`);
          }
          continue;
        }
        
        if (stock.current_stock < item.quantity) {
          if (orderType === 'DEADLINE_DISCOUNT') {
            errors.push(`${item.productName}의 마감할인 재고가 부족합니다 (현재 재고: ${stock.current_stock}개)`);
          } else {
            errors.push(`${item.productName}의 재고가 부족합니다 (현재 재고: ${stock.current_stock}개, 요청: ${item.quantity}개)`);
          }
        }
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      stockData
    };
  },


  /**
   * 재고를 실제로 차감합니다 (주문 완료 시)
   */
  async consumeStock(productId: string, quantity: number): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // 현재 재고 조회 및 업데이트를 직접 SQL로 처리
      const { data: currentStock, error: fetchError } = await supabase
        .from('product_stock')
        .select('current_stock, sold_stock, version')
        .eq('product_id', productId)
        .eq('date', today)
        .single();

      if (fetchError) {
        console.error('Error fetching current stock:', fetchError);
        return {
          success: false,
          error: `재고 정보를 조회할 수 없습니다: ${fetchError.message}`
        };
      }

      if (!currentStock) {
        return {
          success: false,
          error: '해당 날짜의 재고 정보를 찾을 수 없습니다'
        };
      }

      // 재고 부족 체크
      if (currentStock.current_stock < quantity) {
        return {
          success: false,
          error: `재고가 부족합니다 (현재 재고: ${currentStock.current_stock}개, 요청: ${quantity}개)`
        };
      }

      // 재고 차감 업데이트 (optimistic concurrency control 사용)
      const { error: updateError } = await supabase
        .from('product_stock')
        .update({
          current_stock: currentStock.current_stock - quantity,
          sold_stock: currentStock.sold_stock + quantity,
          version: currentStock.version + 1,
          updated_at: new Date().toISOString()
        })
        .eq('product_id', productId)
        .eq('date', today)
        .eq('version', currentStock.version); // 버전 체크로 동시성 제어

      if (updateError) {
        console.error('Error updating stock:', updateError);
        return {
          success: false,
          error: `재고 차감에 실패했습니다: ${updateError.message}`
        };
      }

      console.log(`Successfully consumed stock: ${productId}, quantity: ${quantity}, date: ${today}`);
      return { success: true };
    } catch (error) {
      console.error('Error in consumeStock:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : '재고 차감 중 알 수 없는 오류가 발생했습니다'
      };
    }
  },

  /**
   * 재고를 복구합니다 (결제 실패 시 차감된 재고 복구)
   */
  async restoreStock(productId: string, quantity: number): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // 현재 재고 조회 및 업데이트를 직접 SQL로 처리 (initial_stock 포함)
      const { data: currentStock, error: fetchError } = await supabase
        .from('product_stock')
        .select('current_stock, sold_stock, initial_stock, version')
        .eq('product_id', productId)
        .eq('date', today)
        .single();

      if (fetchError) {
        console.error('Error fetching current stock for restoration:', fetchError);
        return {
          success: false,
          error: `재고 정보를 조회할 수 없습니다: ${fetchError.message}`
        };
      }

      if (!currentStock) {
        return {
          success: false,
          error: '해당 날짜의 재고 정보를 찾을 수 없습니다'
        };
      }

      // 복구할 수량 체크 (sold_stock보다 많이 복구할 수 없음)
      if (currentStock.sold_stock < quantity) {
        return {
          success: false,
          error: `복구할 수량이 판매 수량을 초과합니다 (판매 수량: ${currentStock.sold_stock}개, 복구 요청: ${quantity}개)`
        };
      }

      // initial_stock을 초과하지 않도록 재고 복원량 계산
      const restoredStock = Math.min(
        currentStock.initial_stock,  // initial_stock을 초과하지 않도록 제한
        currentStock.current_stock + quantity
      );
      
      // sold_stock은 0 이하로 내려가지 않도록 제한
      const restoredSold = Math.max(
        0,
        currentStock.sold_stock - quantity
      );

      // 재고 복구 업데이트 (optimistic concurrency control 사용)
      const { error: updateError } = await supabase
        .from('product_stock')
        .update({
          current_stock: restoredStock,  // 상한선이 적용된 재고량
          sold_stock: restoredSold,      // 하한선이 적용된 판매량
          version: currentStock.version + 1,
          updated_at: new Date().toISOString()
        })
        .eq('product_id', productId)
        .eq('date', today)
        .eq('version', currentStock.version); // 버전 체크로 동시성 제어

      if (updateError) {
        console.error('Error restoring stock:', updateError);
        return {
          success: false,
          error: `재고 복구에 실패했습니다: ${updateError.message}`
        };
      }

      console.log(`Successfully restored stock: ${productId}, quantity: ${quantity}, date: ${today}`);
      return { success: true };
    } catch (error) {
      console.error('Error in restoreStock:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : '재고 복구 중 알 수 없는 오류가 발생했습니다'
      };
    }
  },

  // generateTodayStock 메서드 제거됨
  // 재고 생성은 백엔드 Edge Function에서 매일 자정에 자동 처리됩니다
};